/**
 * PDF Side Panel - Saga-style PDF viewer
 * 
 * Features:
 * - Yellow highlighting of citation text directly in the PDF
 * - Full-width layout matching Saga Legal's style
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, FileText, ChevronUp, ChevronDown,
  ZoomIn, ZoomOut, Maximize2, Search, Printer, Download, Loader2, AlertCircle
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import axios from 'axios';
import { auth } from '../../utils/auth';

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Citation {
  id: string;
  documentId: string;
  chunkId: string;
  filename: string;
  section?: string;
  sectionType?: string;
  excerpt: string;
  fullContent?: string;
  isImportant?: boolean;
  relevance: number;
}

interface PDFSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  citation?: Citation;
  documentId?: string;
  inline?: boolean;
}

export const PDFSidePanel: React.FC<PDFSidePanelProps> = ({
  isOpen,
  onClose,
  citation,
  documentId: propDocumentId,
  inline = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [docInfo, setDocInfo] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  
  const documentId = citation?.documentId || propDocumentId;
  const citationId = citation?.id; // Track citation changes

  // Highlight citation text in PDF - SAGA STYLE
  // Approach: Add CSS class to text layer spans for highlighting
  const highlightCitationText = useCallback(() => {
    if (!citation?.excerpt || !pageRef.current) return;
    
    const textLayer = pageRef.current.querySelector('.react-pdf__Page__textContent') as HTMLElement;
    if (!textLayer) return;
    
    const spans = Array.from(textLayer.querySelectorAll('span')) as HTMLSpanElement[];
    if (spans.length === 0) return;
    
    // STEP 1: Clear ALL previous highlights
    spans.forEach(span => {
      span.classList.remove('lexy-highlight');
    });
    
    // Inject CSS if not already done
    if (!document.getElementById('lexy-highlight-styles')) {
      const style = document.createElement('style');
      style.id = 'lexy-highlight-styles';
      style.textContent = `
        .lexy-highlight {
          background: linear-gradient(to bottom, rgba(253, 224, 71, 0.4) 0%, rgba(253, 224, 71, 0.4) 100%) !important;
          border-radius: 2px !important;
          padding: 2px 0 !important;
          margin: -2px 0 !important;
          box-decoration-break: clone !important;
          -webkit-box-decoration-break: clone !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    // STEP 2: Clean and prepare excerpt for matching
    const excerptClean = citation.excerpt
      .replace(/\.{3}$/, '')
      .replace(/\s+/g, ' ')
      .trim();
    
    if (excerptClean.length < 10) return;
    
    // STEP 3: Build full text map from all spans
    let fullText = '';
    const spanData: { span: HTMLSpanElement; start: number; end: number }[] = [];
    
    spans.forEach(span => {
      const text = span.textContent || '';
      spanData.push({ 
        span, 
        start: fullText.length, 
        end: fullText.length + text.length 
      });
      fullText += text;
    });
    
    // Normalize for matching (lowercase, collapse whitespace)
    const fullTextNorm = fullText.toLowerCase().replace(/\s+/g, ' ');
    const excerptNorm = excerptClean.toLowerCase().replace(/\s+/g, ' ');
    
    // STEP 4: Find match using progressive search
    let matchStart = -1;
    let matchLength = 0;
    
    // Try progressively shorter excerpts until we find a match
    const searchLengths = [100, 80, 60, 40, 25, 15];
    for (const len of searchLengths) {
      if (excerptNorm.length >= len) {
        const searchTerm = excerptNorm.slice(0, len);
        const idx = fullTextNorm.indexOf(searchTerm);
        if (idx !== -1) {
          matchStart = idx;
          matchLength = Math.min(excerptNorm.length, len + 50);
          break;
        }
      }
    }
    
    // STEP 5: Fallback - try section header matching
    if (matchStart === -1) {
      const sectionMatch = excerptNorm.match(/^(\d+\.?\s*[a-z]{3,})/i);
      if (sectionMatch) {
        const header = sectionMatch[1];
        const idx = fullTextNorm.indexOf(header);
        if (idx !== -1) {
          matchStart = idx;
          const remaining = fullTextNorm.slice(idx + header.length);
          const nextSection = remaining.search(/\d+\.\s*[a-z]/i);
          matchLength = nextSection > 0 ? header.length + nextSection : 200;
        }
      }
    }
    
    // STEP 6: Final fallback - keyword matching
    if (matchStart === -1) {
      const keywords = excerptNorm.split(/\s+/).filter(w => w.length > 4);
      if (keywords.length > 0) {
        spanData.forEach(({ span }) => {
          const spanText = (span.textContent || '').toLowerCase();
          const matchCount = keywords.filter(kw => spanText.includes(kw)).length;
          if (matchCount >= 2 || (keywords.length === 1 && matchCount === 1)) {
            span.classList.add('lexy-highlight');
          }
        });
        scrollToFirstHighlight(spans);
        return;
      }
      return;
    }
    
    const matchEnd = matchStart + matchLength;
    
    // STEP 7: Apply highlight to matching spans using CSS class
    let highlightedAny = false;
    spanData.forEach(({ span, start, end }) => {
      if (start < matchEnd && end > matchStart) {
        span.classList.add('lexy-highlight');
        highlightedAny = true;
      }
    });
    
    // STEP 8: Scroll to first highlighted span
    if (highlightedAny) {
      scrollToFirstHighlight(spans);
    }
  }, [citation]);
  
  // Scroll to the first highlighted span
  const scrollToFirstHighlight = (spans: HTMLSpanElement[]) => {
    setTimeout(() => {
      const highlighted = spans.find(s => s.classList.contains('lexy-highlight'));
      if (highlighted) {
        highlighted.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  useEffect(() => {
    if (isOpen && documentId) {
      loadDocument();
    }
    
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen, documentId]);

  // Re-highlight when citation changes
  useEffect(() => {
    if (isOpen && pdfUrl && !loading && citationId) {
      // Small delay to ensure text layer is rendered
      const timer = setTimeout(highlightCitationText, 200);
      return () => clearTimeout(timer);
    }
  }, [citationId, isOpen, pdfUrl, loading, highlightCitationText]);

  // Apply highlighting after page renders
  useEffect(() => {
    if (pdfUrl && !loading) {
      // Single delay - text layer should be ready
      const timer = setTimeout(highlightCitationText, 300);
      return () => clearTimeout(timer);
    }
  }, [pdfUrl, loading, currentPage, highlightCitationText]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);

    try {
      const infoRes = await axios.get(`${API}/documents/${documentId}?include_text=true`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      setDocInfo(infoRes.data);

      const filename = infoRes.data.filename || '';
      const isPdf = filename.toLowerCase().endsWith('.pdf');
      
      if (isPdf) {
        try {
          const fileRes = await axios.get(`${API}/documents/${documentId}/view`, {
            headers: { Authorization: `Bearer ${auth.getToken()}` },
            responseType: 'blob'
          });
          
          const blob = new Blob([fileRes.data], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPdfUrl(blobUrl);
        } catch (fileErr: any) {
          console.error('Error loading PDF:', fileErr);
          setPdfUrl(null);
        }
      } else {
        setPdfUrl(null);
      }
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Could not load document');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const goToPrevPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(numPages, prev + 1));
  const zoomIn = () => setScale(prev => Math.min(2.5, prev + 0.2));
  const zoomOut = () => setScale(prev => Math.max(0.5, prev - 0.2));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: inline ? '45%' : 'auto', opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white flex flex-col border-l border-gray-200 h-full"
          style={{ minWidth: 420, maxWidth: inline ? '50%' : 600 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-900 truncate">
                {docInfo?.filename || 'Loading...'}
              </span>
            </div>
            
            <div className="flex items-center gap-1">
              <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Search">
                <Search className="w-4 h-4 text-gray-500" />
              </button>
              
              {pdfUrl && numPages > 0 && (
                <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-100 rounded">
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage <= 1}
                    className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30 transition-colors"
                  >
                    <ChevronUp className="w-4 h-4 text-gray-600" />
                  </button>
                  <span className="text-sm text-gray-700 px-1">
                    {currentPage} of {numPages}
                  </span>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage >= numPages}
                    className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30 transition-colors"
                  >
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              )}
              
              <div className="flex items-center gap-0.5 px-1 py-1 bg-gray-100 rounded">
                <button onClick={zoomOut} className="p-0.5 hover:bg-gray-200 rounded transition-colors">
                  <ZoomOut className="w-4 h-4 text-gray-600" />
                </button>
                <button onClick={zoomIn} className="p-0.5 hover:bg-gray-200 rounded transition-colors">
                  <ZoomIn className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              
              <select 
                value={Math.round(scale * 100)}
                onChange={(e) => setScale(parseInt(e.target.value) / 100)}
                className="text-sm bg-gray-100 border-0 rounded px-2 py-1 text-gray-700"
              >
                <option value={75}>75%</option>
                <option value={100}>100%</option>
                <option value={125}>125%</option>
                <option value={150}>150%</option>
              </select>
              
              <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Print">
                <Printer className="w-4 h-4 text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Download">
                <Download className="w-4 h-4 text-gray-500" />
              </button>
              <button className="p-1.5 hover:bg-gray-100 rounded transition-colors" title="Fullscreen">
                <Maximize2 className="w-4 h-4 text-gray-500" />
              </button>
              <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* PDF Content Area */}
          <div 
            ref={containerRef}
            className="flex-1 overflow-auto bg-gray-200"
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-gray-500 animate-spin" />
                  <p className="text-gray-600">Loading document...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-6">
                  <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <p className="text-red-600 text-lg">{error}</p>
                </div>
              </div>
            ) : pdfUrl ? (
              <div ref={pageRef} className="flex justify-center p-4">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(err) => {
                    console.error('PDF load error:', err);
                    setPdfUrl(null);
                  }}
                  loading={
                    <div className="flex items-center justify-center p-12">
                      <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
                    </div>
                  }
                >
                  <Page 
                    pageNumber={currentPage} 
                    scale={scale}
                    className="shadow-xl bg-white"
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    onRenderSuccess={() => {
                      // Text layer is ready - apply highlighting
                      setTimeout(highlightCitationText, 100);
                    }}
                  />
                </Document>
              </div>
            ) : (
              /* Fallback: Text view with highlighting */
              <div className="p-4">
                <div className="mx-auto shadow-xl bg-white max-w-none">
                  <div className="p-6 border-b border-gray-200">
                    <h1 className="text-xl font-bold text-gray-900 text-center">
                      {docInfo?.filename?.replace(/\.[^/.]+$/, '').replace(/_/g, ' ').toUpperCase() || 'DOCUMENT'}
                    </h1>
                  </div>
                  <div className="p-6">
                    {docInfo?.extracted_text ? (
                      <div className="prose prose-sm max-w-none">
                        {renderTextWithHighlights(docInfo.extracted_text, citation?.excerpt)}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic text-center py-8">
                        No text content available for this document.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Helper function to render text with yellow highlights
function renderTextWithHighlights(text: string, excerpt?: string) {
  if (!excerpt || !text) {
    return text.split('\n\n').map((p, i) => <p key={i} className="mb-4">{p}</p>);
  }
  
  const excerptLower = excerpt.toLowerCase().slice(0, 50);
  const paragraphs = text.split('\n\n');
  
  return paragraphs.map((paragraph, idx) => {
    const paragraphLower = paragraph.toLowerCase();
    const shouldHighlight = excerptLower.split(' ').filter(w => w.length > 3).some(word => 
      paragraphLower.includes(word)
    );
    
    const isHeading = /^[A-ZÆØÅ0-9.\s]+$/.test(paragraph.trim()) && paragraph.trim().length < 100;
    
    if (isHeading) {
      return (
        <h2 key={idx} className="text-lg font-bold text-gray-900 mt-6 mb-3">
          {paragraph}
        </h2>
      );
    }
    
    if (shouldHighlight) {
      return (
        <p key={idx} className="mb-4 bg-yellow-200 px-2 py-1 rounded leading-relaxed">
          {paragraph}
        </p>
      );
    }
    
    return <p key={idx} className="mb-4 leading-relaxed">{paragraph}</p>;
  });
}

export default PDFSidePanel;
